
/**
 * Module dependencies.
 */

import {
  FormControl,
  FormGroup,
  HelpText
} from 'src/components/core/forms/fields-styled-components';

import { media, units } from 'src/styles/utils';
import { regexes } from 'src/core/utils/regexes';
import { useForm } from 'react-hook-form';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import React, { ReactElement, forwardRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  onContinue: (email: string) => void,
  resetSignal?: number
};

/**
 * Field id.
 */

const fieldId = 'newsletter-cta-email';

/**
 * Error id.
 */

const errorId = `${fieldId}-error`;

/**
 * `Form` styled component.
 */

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${units(3)};
  width: 100%;

  ${media.min('ms')`
    align-items: flex-start;
    flex-direction: row;
    gap: ${units(2)};
  `}
`;

/**
 * `Field` styled component.
 *
 * Built on `FormGroup` so the input behaves exactly like every other field of
 * the site — including the `outline: none` reset, without which the browser
 * draws its own focus ring on top of the underline.
 */

const Field = styled(FormGroup)`
  flex: 1 1 0;
  margin-bottom: 0;
  min-width: 0;
`;

/**
 * `Label` styled component.
 *
 * The design has no visible label, only a placeholder, so the label is kept
 * for assistive technology alone.
 */

const Label = styled.label`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

/**
 * `SubmitButton` styled component.
 */

const SubmitButton = styled(Button)`
  width: 100%;

  ${media.min('ms')`
    flex: none;
    width: auto;
  `}
`;

/**
 * `NewsletterCtaForm` component.
 *
 * Step one of the subscription: it only collects the email and hands it over,
 * the remaining fields are asked for in the sidebar. An invalid address is
 * rejected here so the sidebar never opens for one.
 */

const NewsletterCtaForm = (props: Props, ref: any): ReactElement => {
  const { className, onContinue, resetSignal } = props;
  const { translate } = useTranslate();
  const { errors, handleSubmit, register, reset } = useForm({ mode: 'onSubmit' });
  const hasError = !!errors?.email;
  const onSubmit = useCallback(({ email }) => {
    onContinue(email);
  }, [onContinue]);

  /**
   * Clear the field once the subscription goes through, so the address does
   * not linger in a form that has already served its purpose.
   */

  useEffect(() => {
    if (resetSignal) {
      reset();
    }
  }, [reset, resetSignal]);

  return (
    <Form
      className={className}
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Field>
        <Label htmlFor={fieldId}>
          {translate('forms:emailLabel')}
        </Label>

        <FormControl
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError}
          autoComplete={'email'}
          hasError={hasError}
          id={fieldId}
          name={'email'}
          placeholder={translate('forms:emailPlaceholder')}
          ref={register<any>({
            pattern: {
              message: translate('forms:emailPatternValidation'),
              value: regexes.email
            },
            required: {
              message: translate('forms:requiredField'),
              value: true
            }
          })}
          type={'email'}
        />

        {hasError && (
          <HelpText
            hasError
            id={errorId}
            role={'alert'}
          >
            {errors?.email?.message}
          </HelpText>
        )}
      </Field>

      <SubmitButton
        ref={ref}
        type={'submit'}
      >
        {translate('common:actions.subscribe')}
      </SubmitButton>
    </Form>
  );
};

/**
 * Export `NewsletterCtaForm` component.
 */

export default forwardRef<HTMLButtonElement, Props>(NewsletterCtaForm);
